/*
 * IMPORTS
 */
import React from "react"; // Npm: react.js library.
import PropTypes from "prop-types"; // Npm: react.js library.
import JoiBrowser from "joi-browser"; // Npm: Joi for frontend validation.
import _ from "underscore"; // Npm: Underscore.js library.
import { toast } from "react-hot-toast"; // Npm: React hot toast.
import { connect } from "react-redux"; // Npm: React Redux for state management.
import { useLazyQuery, useMutation } from "@apollo/client"; // Npm: Apollo client.
import { HiMinusCircle, HiPlusCircle } from "react-icons/hi"; // Npm: React icons.
import { Button, Flex, useBreakpointValue } from "@chakra-ui/react"; // Npm: Chakra UI components.

/*
 * PACKAGES
 */
import SubmitButton from "components/SubmitButton";
import MccMncSelector from "components/MccMncSelector";
import VendorAccountSelector from "components/VendorAccountSelector";
import { MemoizedInput, MemoizedSelect } from "components/MemoizedInput";

/*
 * GRAPHS
 */
import FallbackRoutePlanReadQuery from "./__query__/index.fallbackroutePlan.read.query";
import FallbackRoutePlanUpdateMutation from "./__mutation__/index.fallbackroutePlan.update.mutation";
import FallbackRoutePlanCreateMutation from "./__mutation__/index.fallbackroutePlan.create.mutation";
import VendorAccountAttachFallbackRoutePlanMutation from "./__mutation__/index.vendorAccount.attachfallbackRoutePlan.mutation";

/*
 * OBJECTS
 */
const Index = ({ isOpen, isCreateOnly, onClose, passOn, refetch }) => {
  // Hook assignment.
  const [vendorAccount, setVendorAccount] = React.useState(void 0);
  const [mappingForms, setMappingForms] = React.useState([1]);
  const [error, setError] = React.useState("");
  const [formVersion, _ResetFormInputs] = React.useReducer(j => j + 1, 0);
  const [, _TriggerRender] = React.useReducer(j => j + 1, 0);
  const [selectedVendors, setSelectedVendors] = React.useState({});
  const [QueryFallbackRoutePlanRead, QueryFallbackRoutePlanReadResponse] =
    useLazyQuery(FallbackRoutePlanReadQuery);
  const [
    FallbackMutationRoutePlanCreate,
    FallbackMutationRoutePlanCreateResponse,
  ] = useMutation(FallbackRoutePlanCreateMutation);
  const [
    FallbackMutationRoutePlanUpdate,
    FallbackMutationRoutePlanUpdateResponse,
  ] = useMutation(FallbackRoutePlanUpdateMutation);
  const [
    MutationVendorAccountAttachFallbackRoutePlan,
    MutationVendorAccountAttachFallbackRoutePlanResponse,
  ] = useMutation(VendorAccountAttachFallbackRoutePlanMutation);
  const _formDataRef = React.useRef({});
  const _isCurrentViewMobile = useBreakpointValue({
    base: false,
    md: false,
    lg: false,
    xl: false,
    sm: true,
    xs: true,
  });

  // Object assignment.
  const _AttachVendorAccountToFallbackRoutePlan = async (__id) => {
    // Only Execute if route has data.
    if (
      !_.isEmpty(vendorAccount) &&
      vendorAccount.includes("(") &&
      vendorAccount.includes(")")
    ) {
      // Const assignment.
      const vendorAccountId = vendorAccount
        .split(" ")[1]
        .replace("(", "")
        .replace(")", "");
      // Execute mutation.
      const _MutationVendorAccountAttachFallbackRoutePlan =
        await MutationVendorAccountAttachFallbackRoutePlan({
          variables: {
            routePlanId: __id,
            vendorAccountId: vendorAccountId,
          },
        });

      // If mutation caught an exception then report failure.
      if (_MutationVendorAccountAttachFallbackRoutePlan instanceof Error)
        return _MutationVendorAccountAttachFallbackRoutePlan;

      // Style Guide.
      toast(
        _.every(
          _.pluck(
            _MutationVendorAccountAttachFallbackRoutePlan?.data
              ?.VendorAccountAttachFallbackRoutePlan,
            "status",
          ),
          (i) => "UPDATE_SUCCESSFUL" === i,
        )
          ? "Successfully attached the vendor with CBR routePlan"
          : "Something went wrong. Please try again after some time.",
      );

      // On successful bind close modal.
      // if (_.every(_.pluck(_MutationVendorAccountAttachFallbackRoutePlan?.data?.VendorAccountAttachFallbackRoutePlan, 'status'), i => 'UPDATE_SUCCESSFUL' === i)) onClose()
    }

    // Report void 0.
    return void 0;
  };
  const _SubmitForm = async (e) => {
    // Prevent default behavior.
    e.preventDefault();

    // Reset error.
    setError("");

    // Const assignment.
    const _JoiSchema = JoiBrowser.object({
      mcc: JoiBrowser.string().required(),
      mnc: JoiBrowser.array().items(JoiBrowser.string().required()).required(),
      mappings: JoiBrowser.array()
        .items(
          JoiBrowser.object({
            vendorAccount: JoiBrowser.string().required(),
            priority: JoiBrowser.number().greater(0).less(6).required(),
          }),
        )
        .min(1)
        .required(),
    }).options({ allowUnknown: true });

    // Make sure that mnc is array.
    if (!isCreateOnly && !_.isArray(_formDataRef.current?.mnc))
      _formDataRef.current = {
        ..._formDataRef.current,
        mnc: [_formDataRef.current?.mnc?.split("(")[1]?.split(")")[0]],
      };

    const _mappings = _.compact(
      mappingForms.map((__form, __index) => {
        const _vendorAccount =
          _formDataRef.current?.[`vendorAccount__${__index}`];
        const _priority = _formDataRef.current?.[`priority__${__index}`];

        if (_.isEmpty(_vendorAccount) && !_priority) return void 0;
        return { vendorAccount: _vendorAccount, priority: _priority };
      }),
    );

    if (_.isEmpty(_mappings)) return setError("vendorAccount is required.");
    if (_.some(_mappings, (j) => _.isEmpty(j?.vendorAccount)))
      return setError("vendorAccount is required.");
    if (
      _.some(
        _mappings,
        (j) => !j?.priority || j?.priority < 1 || j?.priority > 5,
      )
    )
      return setError("priority is required.");

    _formDataRef.current = {
      ..._.pick(_formDataRef.current, _.identity),
      mappings: _mappings,
      fallbackTimeout: Number(_formDataRef.current?.fallbackTimeout ?? 0),
    };

    // Validate form data.
    const _JoiSchemaValidate = _JoiSchema.validate(_formDataRef.current);

    // If error exists then report failure.
    if (_JoiSchemaValidate.error)
      return setError(_JoiSchemaValidate.error?.message);

    /*
     * Update _formDataRef with routePlanId
     * if it doesn't exist and also make sure that mode
     * is not create only.
     */
    if (!isCreateOnly && _.isEmpty(_formDataRef.current?.routePlanId))
      _formDataRef.current = {
        ..._formDataRef.current,
        routePlanId: passOn?.routePlanId,
      };
    if (isCreateOnly && _.isEmpty(_formDataRef.current?.routeId))
      _formDataRef.current = {
        ..._formDataRef.current,
        routeId: passOn?.routeId,
      };

    /*
     * Update _formDataRef with input and routePlan
     * if it doesn't exist. Also make sure that mode is
     * not create only.
     */
    _formDataRef.current = {
      ..._formDataRef.current,
      input: [{ ..._.omit(_formDataRef.current, "input") }],
    };

    // Execute update mutation.
    const _FallbackMutationRoutePlanUpdate = await [
      isCreateOnly
        ? FallbackMutationRoutePlanCreate
        : FallbackMutationRoutePlanUpdate,
    ]?.[0]({
      variables: {
        routePlanId: _.isEmpty(passOn?.routePlanIds)
          ? isCreateOnly
            ? ""
            : passOn?.routePlanId
          : void 0,
        routePlanIds: _.isEmpty(passOn?.routePlanIds)
          ? void 0
          : isCreateOnly
            ? ""
            : passOn?.routePlanIds,

        input: _.pick(_formDataRef.current, "input").input.map((j) => {
          let _mcc, _mnc;

          // Create case: send mcc, mnc, and mappings array with vendorAccountId and priority.
          if (isCreateOnly) {
            let _mappings = _.compact(
              (j?.mappings ?? []).map((r) => {
                let _vendorAccount = r?.vendorAccount;
                if (
                  r?.vendorAccount &&
                  (r?.vendorAccount?.includes("(") ||
                    r?.vendorAccount?.includes(")"))
                )
                  _vendorAccount = r?.vendorAccount.split("(")[1].split(")")[0];
                return {
                  vendorAccountId: _vendorAccount,
                  priority: r?.priority,
                };
              }),
            );
            _mcc = j?.mcc;
            _mnc = j?.mnc;
            if (j?.mcc && (j?.mcc?.includes("(") || j?.mcc?.includes(")")))
              _mcc = j.mcc.split("(")[1].split(")")[0];
            if (j?.mnc && 0 < j?.mnc?.length)
              _mnc = _.compact(
                _.flatten(
                  j?.mnc.map((r) =>
                    r?.includes("(") && r?.includes(")")
                      ? r.split("(")[1].split(")")[0]
                      : r,
                  ),
                ),
              );
            return {
              mcc: _mcc,
              mnc: _mnc,
              mappings: _mappings,
              routeId: passOn?.routeId,
              fallbackTimeout: Number(j?.fallbackTimeout ?? 0),
            };
          }

          // Update case: send single vendor priority
          let _vendorAccount = j?.mappings?.[0]?.vendorAccount;
          let _priority = j?.mappings?.[0]?.priority;
          if (
            _vendorAccount &&
            (_vendorAccount.includes("(") || _vendorAccount.includes(")"))
          )
            _vendorAccount = _vendorAccount.split("(")[1].split(")")[0];
          _mcc = j?.mcc;
          _mnc = j?.mnc;
          if (j?.mcc && (j?.mcc?.includes("(") || j?.mcc?.includes(")")))
            _mcc = j.mcc.split("(")[1].split(")")[0];
          if (j?.mnc && 0 < j?.mnc?.length)
            _mnc = _.compact(
              _.flatten(
                j?.mnc.map((r) =>
                  r?.includes("(") && r?.includes(")")
                    ? r.split("(")[1].split(")")[0]
                    : r,
                ),
              ),
            );

          return {
            vendorAccountId: _vendorAccount,
            mcc: _mcc,
            mnc: _mnc,
            priority: _priority,
            fallbackTimeout: Number(j?.fallbackTimeout ?? 0),
          };
        }),
      },
    });

    // If mutation caught an exception then report failure.
    if (_FallbackMutationRoutePlanUpdate instanceof Error)
      return _FallbackMutationRoutePlanUpdate;

    // Local variable.
    let _routePlanIds;

    // If Update contains data then update routePlanIds.
    if (
      !_.isEmpty(
        _FallbackMutationRoutePlanUpdate?.data?.FallbackRoutePlanUpdate,
      )
    )
      _routePlanIds = _.compact(
        _.pluck(
          _FallbackMutationRoutePlanUpdate?.data?.FallbackRoutePlanUpdate,
          "id",
        ),
      );
    if (
      !_.isEmpty(
        _FallbackMutationRoutePlanUpdate?.data?.FallbackRoutePlanCreate,
      )
    )
      _routePlanIds = _.compact(
        _.pluck(
          _FallbackMutationRoutePlanUpdate?.data?.FallbackRoutePlanCreate,
          "id",
        ),
      );

    // Check if create was successful.
    if (
      !_.isEmpty(
        _FallbackMutationRoutePlanUpdate?.data?.FallbackRoutePlanCreate,
      ) &&
      _.every(
        _.pluck(
          _FallbackMutationRoutePlanUpdate?.data?.FallbackRoutePlanCreate,
          "status",
        ) ?? [],
        (j) => "CREATE_SUCCESSFUL" === j,
      )
    ) {
      // Style guide.
      toast("Successfully upserted the route plan.");

      // On successful create close modal.
      onClose?.();
      await refetch?.();
      return;
    }
    // Check if update was successful.
    if (
      !_.isEmpty(
        _FallbackMutationRoutePlanUpdate?.data?.FallbackRoutePlanUpdate,
      ) &&
      _.every(
        _.pluck(
          _FallbackMutationRoutePlanUpdate?.data?.FallbackRoutePlanUpdate,
          "status",
        ) ?? [],
        (j) => "UPDATE_SUCCESSFUL" === j,
      )
    ) {
      // Attach vendor account and route to customer.
      !_.isEmpty(_routePlanIds) &&
        _.isArray(_routePlanIds) &&
        (await _AttachVendorAccountToFallbackRoutePlan(_routePlanIds));
      // Style guide.
      toast("Successfully upserted the route plan.");

      // Report failure.
      onClose?.();
      await refetch?.();
      return;
    }

    // Style Guide.
    toast(
      _.isEmpty(_FallbackMutationRoutePlanUpdate?.data?.FallbackRoutePlanCreate)
        ? [
            0 ===
            _.pluck(
              _FallbackMutationRoutePlanUpdate?.data?.FallbackRoutePlanUpdate,
              "message",
            )?.length
              ? "Same plan already exists. kindly delete it or modify it instead."
              : _.pluck(
                  _FallbackMutationRoutePlanUpdate?.data
                    ?.FallbackRoutePlanUpdate,
                  "message",
                ),
          ]?.join(" ")
        : _.pluck(
            _FallbackMutationRoutePlanUpdate?.data?.FallbackRoutePlanCreate,
            "message",
          )?.join(" "),
    );

    // Report void.
    return void 0;
  };

  // Event handler.
  React.useEffect(() => {
    // Only prepare the form while the modal is open.
    if (!isOpen) return;

    // Create mode should reset local form state without hitting read query.
    if (isCreateOnly) {
      _formDataRef.current = {};
      setMappingForms([1]);
      setSelectedVendors({});
      setVendorAccount(void 0);
      setError("");
      _ResetFormInputs();
      return;
    }

    if (_.isEmpty(passOn?.routePlanId)) return;

    let _isActive = true;

    // _Async handler.
    const _Async = async () => {
      // Const assignment.
      const _QueryFallbackRoutePlanReadQuery = await QueryFallbackRoutePlanRead(
        { variables: { routePlanId: passOn?.routePlanId } },
      );

      if (!_isActive) return void 0;

      // If query caught an exception then report failure.
      if (_QueryFallbackRoutePlanReadQuery instanceof Error)
        return _QueryFallbackRoutePlanReadQuery;

      /*
       * If routePlan details fetch complete then
       * update its value.
       */
      if (_QueryFallbackRoutePlanReadQuery?.data?.FallbackRoutePlanRead) {
        // Const assignment.
        const _routePlanRead = _.first(
          _QueryFallbackRoutePlanReadQuery?.data?.FallbackRoutePlanRead,
        );

        // Update form data.
        const _priorityValue = parseInt(_routePlanRead?.priority, 10);
        _formDataRef.current = {
          routePlanId: passOn?.routePlanId,
          createdAt: _routePlanRead?.createdAt,
          updatedAt: _routePlanRead?.updatedAt,
          mcc: _routePlanRead?.Mcc?.mcc
            ? `${_routePlanRead?.Mcc?.countryName} (${_routePlanRead?.Mcc?.mcc})`
            : void 0,
          mnc: _routePlanRead?.Mnc?.mnc
            ? `(${_routePlanRead?.Mnc?.mnc}) ${_routePlanRead?.Mnc?.network}`
            : void 0,
          countryName: _routePlanRead?.Mcc?.countryName,
          fallbackTimeout: _routePlanRead?.fallbackTimeout ?? 0,
          priority__0: _priorityValue,
          vendorAccount__0:
            0 <
            _QueryFallbackRoutePlanReadQuery?.data?.FallbackRoutePlanRead
              ?.length
              ? _routePlanRead?.VendorAccount?.displayName
                ? `${_routePlanRead?.VendorAccount?.displayName} (${_routePlanRead?.VendorAccount?.id})`
                : void 0
              : void 0,
        };
        setMappingForms([1]);
        setSelectedVendors({});

        // Reset uncontrolled inputs once loaded data is ready.
        return _ResetFormInputs();
      }

      // Report failure.
      return void 0;
    };
    _Async();

    return () => {
      _isActive = false;
    };
  }, [
    QueryFallbackRoutePlanRead,
    _ResetFormInputs,
    isCreateOnly,
    isOpen,
    passOn?.routePlanId,
  ]);
  // Const assignment.
  const _isLoading =
    MutationVendorAccountAttachFallbackRoutePlanResponse.loading ||
    FallbackMutationRoutePlanUpdateResponse.loading ||
    FallbackMutationRoutePlanCreateResponse.loading;
  const _isInputDisabled = isCreateOnly
    ? false
    : _isLoading || QueryFallbackRoutePlanReadResponse.loading;

  // Return component.
  return (
    <form onSubmit={_SubmitForm} className="routePlanUpsert" key={formVersion}>
      <Flex
        gap="22px"
        flexDir="column"
        wrap="nowrap"
        w="100%"
        overflow="visible"
      >
        <Flex width="100%" gap="18px" direction="column" overflow="visible">
          {mappingForms.map((__form, __index) => (
            <Flex
              key={`mapping__${__index}`}
              width="100%"
              gap="18px"
              direction={{ base: "column", md: "row" }}
              alignItems={{ base: "stretch", md: "flex-end" }}
              position="relative"
              zIndex={mappingForms.length - __index}
            >
              <VendorAccountSelector
                disabled={_isInputDisabled}
                name={`vendorAccount__${__index}`}
                label="Vendor Account"
                placeholder='e.g. "Awesome Vendor Account"'
                data={
                  selectedVendors[`vendorAccount__${__index}`] ??
                  _formDataRef?.current?.[`vendorAccount__${__index}`]
                }
                isRequired={true}
                error={error}
                height="40px"
                isInvalid={error?.includes("vendorAccount")}
                onChange={({ target }) => {
                  const { name, value } = target;
                  _formDataRef.current = {
                    ..._formDataRef?.current,
                    [name]: value,
                  };
                  setSelectedVendors((prev) => ({ ...prev, [name]: value }));
                  setVendorAccount(value);
                }}
              />
              <MemoizedSelect
                key={`priority__${__index}__${formVersion}`}
                disabled={_isInputDisabled}
                name={`priority__${__index}`}
                label="Priority"
                placeholder="Select Priority"
                data={_formDataRef?.current?.[`priority__${__index}`]}
                error={error}
                isInvalid={error?.includes("priority")}
                options={[1, 2, 3, 4, 5]}
                onChange={({ target }) => {
                  // Over spreading.
                  const { name, value } = target;
                  const priorityValue = parseInt(value, 10);

                  // Update form data.
                  _formDataRef.current = {
                    ..._formDataRef.current,
                    [name]: priorityValue,
                  };

                  // Clear error if priority is selected
                  if (error?.includes("priority")) {
                    setError("");
                  }
                }}
              />
              <Button
                disabled={_isInputDisabled}
                variant="basic"
                type="button"
                h="40px"
                minH="40px"
                bg={
                  __index === mappingForms.length - 1 ? "brand.400" : "red.400"
                }
                onClick={() => {
                  if (__index !== mappingForms.length - 1) {
                    const temp = _.pick(
                      _formDataRef.current,
                      (__, key) =>
                        !key.startsWith("vendorAccount__") &&
                        !key.startsWith("priority__"),
                    );
                    let newIndex = 0;

                    mappingForms.forEach((__formItem, i) => {
                      if (i !== __index) {
                        temp[`vendorAccount__${newIndex}`] =
                          _formDataRef.current[`vendorAccount__${i}`];
                        temp[`priority__${newIndex}`] =
                          _formDataRef.current[`priority__${i}`];
                        newIndex++;
                      }
                    });

                    _formDataRef.current = temp;
                    setMappingForms(
                      mappingForms.filter((u, i) => i !== __index),
                    );
                    _TriggerRender();
                    return;
                  }

                  setMappingForms([...mappingForms, 1]);
                }}
                _hover={{
                  bg:
                    __index === mappingForms.length - 1
                      ? "brand.500"
                      : "red.500",
                }}
                _active={{
                  bg:
                    __index === mappingForms.length - 1
                      ? "brand.600"
                      : "red.600",
                }}
                minW="50px"
              >
                {__index === mappingForms.length - 1 ? (
                  <HiPlusCircle size={16} />
                ) : (
                  <HiMinusCircle size={16} />
                )}
              </Button>
            </Flex>
          ))}
        </Flex>

        {/* MCC + MNC */}
        <Flex width="100%" gap="22px" direction={{ base: "column", md: "row" }}>
          <MccMncSelector
            isRequired={true}
            disabled={_isInputDisabled}
            mccValue={_formDataRef?.current?.mcc}
            mncValue={_formDataRef?.current?.mnc}
            inValidMnc={error?.includes("mnc")}
            inValidMcc={error?.includes("mcc")}
            onChange={(i) => {
              // Update form data.
              _formDataRef.current = {
                ..._formDataRef.current,
                mnc: i.mnc,
                mcc: i.mcc,
              };
            }}
          />
          <MemoizedInput
            disabled={_isInputDisabled}
            isRequired={false}
            name="fallbackTimeout"
            label="Response time"
            placeholder='e.g. "3 is 3 seconds"'
            onChange={({ target }) => {
              // Over spreading.
              const { value } = target;
              const _fallbackTimeout = Number(value ?? 0);

              // Make sure that value is in seconds.
              if (0 > _fallbackTimeout)
                return setError(
                  "fallbackTimeout must be greater than or equal to 0",
                );

              // Update form data.
              _formDataRef.current = {
                ..._formDataRef?.current,
                fallbackTimeout: _fallbackTimeout * 1000,
              };

              // Return void.
              return void 0;
            }}
            error={error}
            isInvalid={error?.includes("fallbackTimeout")}
            data={
              0 < _formDataRef?.current?.fallbackTimeout
                ? _formDataRef?.current?.fallbackTimeout / 1000
                : 0
            }
          />
        </Flex>
      </Flex>

      {/* Submit Button */}
      <Flex
        mt="22px"
        alignItems={_isCurrentViewMobile ? "flex-end" : "center"}
        justifyContent="flex-end"
        flexDir="row"
      >
        <SubmitButton
          mt={0}
          disabled={_isInputDisabled}
          onSubmit={_SubmitForm}
          defaultText={
            isCreateOnly
              ? "Create Fallback RoutePlan"
              : "Update Fallback RoutePlan"
          }
          isLoading={_isLoading}
        />
      </Flex>
    </form>
  );
};

/*
 * PROPTYPES
 */
Index.propTypes = {
  isCreateOnly: PropTypes.bool,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  refetch: PropTypes.func,
  passOn: PropTypes.object,
  submitRef: PropTypes.object,
  disabled: PropTypes.bool,
  isUpsertMixerContext: PropTypes.bool,
  setSubmitIsLoading: PropTypes.func,
};

/*
 * REDUX
 */
const _MapStateToProps = (__state) => ({ passOn: __state.PassOn });

/*
 * EXPORT
 */
export default connect(_MapStateToProps)(React.memo(Index));
